import { useEffect, useMemo, useState } from 'react'
import { ArrowLeft, BarChart3, CalendarDays, Download, Loader2, RefreshCw, Users, Wallet } from 'lucide-react'
import api from '../../utils/api'
import { exportRowsToExcel } from '../../utils/exportExcel'

function formatDateText(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function addDays(dateText, days) {
  const [year, month, day] = String(dateText).split('-').map((item) => parseInt(item, 10))
  const date = new Date(year, month - 1, day)
  date.setDate(date.getDate() + days)
  return formatDateText(date)
}

function getDefaultStartDate(days) {
  return addDays(formatDateText(new Date()), -(days - 1))
}

function formatAmount(value) {
  const amount = Number(value || 0)
  return Number.isFinite(amount) ? amount.toFixed(2) : '0.00'
}

function formatAmountOne(value) {
  const amount = Number(value || 0)
  return Number.isFinite(amount) ? amount.toFixed(1) : '0.0'
}

function getWeekdayText(dateText) {
  const [year, month, day] = String(dateText).split('-').map((item) => parseInt(item, 10))
  const date = new Date(year, month - 1, day)
  return ['周日', '周一', '周二', '周三', '周四', '周五', '周六'][date.getDay()]
}

function CompareLine({ yesterdayValue, lastWeekValue, yesterdayTitle = '昨日数据', lastWeekTitle }) {
  return (
    <div className="mt-0.5">
      <span className="cursor-help text-blue-500" title={yesterdayTitle}>{yesterdayValue}</span>
      <span className="text-gray-400">/</span>
      <span className="cursor-help text-orange-500" title={lastWeekTitle}>{lastWeekValue}</span>
    </div>
  )
}

function formatDateTimeText(value) {
  if (!value) return '--'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    const text = String(value).trim()
    if (!text) return '--'
    return text.length >= 16 ? text.slice(0, 16).replace('T', ' ') : text
  }
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${year}-${month}-${day} ${hours}:${minutes}`
}

function formatTimeText(value) {
  const text = formatDateTimeText(value)
  return text === '--' ? text : text.slice(-5)
}

const CURVE_CHART = {
  width: 760,
  height: 280,
  paddingTop: 20,
  paddingRight: 20,
  paddingBottom: 36,
  paddingLeft: 48
}

function getCurveX(index, total, width) {
  if (total <= 1) return width / 2
  return (index / (total - 1)) * width
}

function getCurveY(value, height, maxValue) {
  const safeMax = Math.max(Number(maxValue || 0), 1)
  return height - (Number(value || 0) / safeMax) * height
}

function buildCurvePath(points, width, height, maxValue) {
  if (!Array.isArray(points) || points.length === 0) return ''
  return points
    .map((item, index) => {
      const x = getCurveX(index, points.length, width)
      const y = getCurveY(item.online_users, height, maxValue)
      return `${index === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`
    })
    .join(' ')
}

function buildYAxisTicks(maxValue) {
  const segmentCount = 4
  const safeMax = Math.max(Number(maxValue || 0), 1)
  const step = Math.max(Math.ceil(safeMax / segmentCount), 1)
  const axisMax = step * segmentCount
  const ticks = Array.from({ length: segmentCount + 1 }, (_, index) => ({
    value: index * step,
    ratio: axisMax === 0 ? 0 : (index * step) / axisMax
  }))
  return { axisMax, ticks }
}

function buildXAxisLabels(points, width, formatLabel) {
  if (!Array.isArray(points) || points.length === 0) return []
  if (points.length === 1) {
    return [{
      key: points[0].snapshot_minute || 'only',
      text: formatLabel(points[0].snapshot_minute),
      x: width / 2,
      anchor: 'middle'
    }]
  }

  const targetCount = Math.min(6, points.length)
  const indexes = Array.from({ length: targetCount }, (_, index) => (
    Math.round((index * (points.length - 1)) / (targetCount - 1))
  )).filter((value, index, array) => array.indexOf(value) === index)

  return indexes.map((pointIndex, index) => ({
    key: points[pointIndex].snapshot_minute || `${pointIndex}-${index}`,
    text: formatLabel(points[pointIndex].snapshot_minute),
    x: getCurveX(pointIndex, points.length, width),
    anchor: index === 0 ? 'start' : index === indexes.length - 1 ? 'end' : 'middle'
  }))
}

export default function RegistrationUsageSummaryTab() {
  const getDefaultEndDate = () => formatDateText(new Date())
  const [loading, setLoading] = useState(true)
  const [detailLoading, setDetailLoading] = useState(false)
  const [startDate, setStartDate] = useState(getDefaultStartDate(7))
  const [endDate, setEndDate] = useState(getDefaultEndDate())
  const [filters, setFilters] = useState({ startDate: getDefaultStartDate(7), endDate: getDefaultEndDate() })
  const [detailView, setDetailView] = useState('summary')
  const [summary, setSummary] = useState({
    total_registered: 0,
    total_recharge_users: 0,
    total_first_recharge_users: 0,
    total_first_recharge_same_day: 0,
    total_first_recharge_diff_day: 0,
    total_repeat_recharge_users: 0,
    total_recharge_amount: 0,
    total_official_recharge_amount: 0,
    total_ly360_recharge_amount: 0,
    total_ly360top_recharge_amount: 0,
    total_same_day_ly360top_amount: 0,
    current_online_users: 0,
    today_5min_online_peak: 0,
    today_5min_online_peak_time: null,
    total_usage_users: 0,
    total_second_day_usage_users: 0,
    total_old_usage_users: 0,
    total_paid_old_usage_users: 0,
    total_unpaid_old_usage_users: 0,
    total_heavy_usage_users: 0
  })
  const [daily, setDaily] = useState([])
  const [todayCompare, setTodayCompare] = useState(null)
  const todayText = formatDateText(new Date())
  const lastWeekTitle = `上周${getWeekdayText(addDays(todayText, -7))}数据`
  const [range, setRange] = useState({ start_date: getDefaultStartDate(7), end_date: formatDateText(new Date()), days: 7 })
  const [onlinePeakHistory, setOnlinePeakHistory] = useState([])
  const [todayOnlineCurve, setTodayOnlineCurve] = useState({
    current_online_users: 0,
    today_5min_online_peak: 0,
    today_5min_online_peak_time: null,
    points: [],
    online_user_list: []
  })

  const fetchData = async (nextFilters = filters, options = {}) => {
    const { silent = false } = options
    if (!silent) {
      setLoading(true)
    }
    try {
      const response = await api.get('/admin/registration-usage-summary', {
        params: {
          start_date: nextFilters.startDate,
          end_date: nextFilters.endDate,
          days: nextFilters.days || 7
        }
      })

      if (response.data.success) {
        const data = response.data.data || response.data || {}
        setSummary(data.summary || {})
        setDaily(data.daily || [])
        setTodayCompare(data.today_compare || null)
        setRange({
          start_date: data.start_date || nextFilters.startDate,
          end_date: data.end_date || nextFilters.endDate,
          days: data.days || 7
        })
      }
    } catch (error) {
      console.error('获取注册与使用概览失败:', error)
    } finally {
      if (!silent) {
        setLoading(false)
      }
    }
  }

  useEffect(() => {
    fetchData(filters)
  }, [])

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (document.hidden) {
        return
      }
      fetchData(filters, { silent: true })
    }, 60000)

    return () => {
      window.clearInterval(timer)
    }
  }, [filters])

  // 曲线页每60秒自动刷新
  useEffect(() => {
    if (detailView !== 'today-online-curve') return
    const timer = window.setInterval(() => {
      if (document.hidden) return
      openTodayOnlineCurve(true)
    }, 60000)
    return () => window.clearInterval(timer)
  }, [detailView])

  const handleSubmit = (e) => {
    e.preventDefault()
    const nextFilters = { startDate, endDate }
    setFilters(nextFilters)
    fetchData(nextFilters)
  }

  const handleQuickRange = (nextDays) => {
    const nextStartDate = getDefaultStartDate(nextDays)
    const nextEndDate = getDefaultEndDate()
    setStartDate(nextStartDate)
    setEndDate(nextEndDate)
    const nextFilters = { startDate: nextStartDate, endDate: nextEndDate }
    setFilters(nextFilters)
    fetchData(nextFilters)
  }

  const handleReset = () => {
    const nextDays = 7
    const nextStartDate = getDefaultStartDate(nextDays)
    const nextEndDate = getDefaultEndDate()
    setStartDate(nextStartDate)
    setEndDate(nextEndDate)
    const nextFilters = { startDate: nextStartDate, endDate: nextEndDate }
    setFilters(nextFilters)
    fetchData(nextFilters)
  }

  const rangeText = useMemo(() => {
    return `${range.start_date} 至 ${range.end_date}`
  }, [range])

  const displayDaily = useMemo(() => {
    return [...daily].sort((a, b) => String(b.date).localeCompare(String(a.date)))
  }, [daily])

  const handleExport = () => {
    if (displayDaily.length === 0) {
      return
    }

    const truncated = displayDaily.length > 1000
    const totalRow = {
      date: '合计',
      registered_count: summary.total_registered,
      recharge_user_count: summary.total_recharge_users,
      first_recharge_user_count: summary.total_first_recharge_users,
      repeat_recharge_user_count: summary.total_repeat_recharge_users,
      new_user_first_only_count: summary.total_new_user_first_only,
      new_user_repeat_count: summary.total_new_user_repeat,
      first_recharge_same_day_count: summary.total_first_recharge_same_day,
      first_recharge_diff_day_count: summary.total_first_recharge_diff_day,
      official_recharge_amount: summary.total_official_recharge_amount,
      ly360_recharge_amount: summary.total_ly360_recharge_amount,
      ly360top_recharge_amount: summary.total_ly360top_recharge_amount,
      same_day_official_amount: summary.total_same_day_official_amount,
      same_day_ly360_amount: summary.total_same_day_ly360_amount,
      same_day_ly360top_amount: summary.total_same_day_ly360top_amount,
      usage_user_count: summary.total_usage_users,
      second_day_usage_user_count: summary.total_second_day_usage_users,
      old_usage_user_count: summary.total_old_usage_users,
      paid_old_usage_user_count: summary.total_paid_old_usage_users,
      unpaid_old_usage_user_count: summary.total_unpaid_old_usage_users,
      heavy_usage_user_count: summary.total_heavy_usage_users
    }
    const rows = [...displayDaily.slice(0, 1000), totalRow]
    exportRowsToExcel({
      fileName: `注册与使用概览_${range.start_date}_${range.end_date}${truncated ? '_最新1000行' : ''}`,
      sheetName: '注册与使用概览',
      columns: [
        { header: '日期', key: 'date', width: 14 },
        { header: '注册人数', value: (row) => Number(row.registered_count || 0), width: 10 },
        { header: '充值用户数', value: (row) => Number(row.recharge_user_count || 0), width: 12 },
        { header: '首充用户数', value: (row) => Number(row.first_recharge_user_count || 0), width: 12 },
        { header: '多充用户数', value: (row) => Number(row.repeat_recharge_user_count || 0), width: 12 },
        { header: '新用户首充支付数', value: (row) => Number(row.new_user_first_only_count || 0), width: 16 },
        { header: '新用户多充支付数', value: (row) => Number(row.new_user_repeat_count || 0), width: 16 },
        { header: '今日首充', value: (row) => Number(row.first_recharge_same_day_count || 0), width: 10 },
        { header: '非今日首充', value: (row) => Number(row.first_recharge_diff_day_count || 0), width: 12 },
        { header: '官网支付额', value: (row) => Number(formatAmount(row.official_recharge_amount)), width: 12, format: '0.00' },
        { header: '360支付额', value: (row) => Number(formatAmount(row.ly360_recharge_amount)), width: 12, format: '0.00' },
        { header: '360(top)支付额', value: (row) => Number(formatAmount(row.ly360top_recharge_amount)), width: 14, format: '0.00' },
        { header: '官网新用户支付额', value: (row) => Number(formatAmount(row.same_day_official_amount)), width: 16, format: '0.00' },
        { header: '360新用户支付额', value: (row) => Number(formatAmount(row.same_day_ly360_amount)), width: 16, format: '0.00' },
        { header: '360(top)新用户支付额', value: (row) => Number(formatAmount(row.same_day_ly360top_amount)), width: 18, format: '0.00' },
        { header: '使用人数(含老用户)', value: (row) => Number(row.usage_user_count || 0), width: 18 },
        { header: '第二天使用人数', value: (row) => Number(row.second_day_usage_user_count || 0), width: 16 },
        { header: '老用户使用人数', value: (row) => Number(row.old_usage_user_count || 0), width: 16 },
        { header: '已充值老用户', value: (row) => Number(row.paid_old_usage_user_count || 0), width: 14 },
        { header: '未充值老用户', value: (row) => Number(row.unpaid_old_usage_user_count || 0), width: 14 },
        { header: '重度用户使用人数', value: (row) => Number(row.heavy_usage_user_count || 0), width: 16 }
      ],
      rows
    })

    if (truncated) {
      alert('数据超过 1000 行，已导出最新 1000 行')
    }
  }

  const curveChartData = useMemo(() => {
    const points = Array.isArray(todayOnlineCurve.points) ? todayOnlineCurve.points : []
    if (points.length === 0) {
      return {
        path: '',
        maxValue: 1,
        xAxisLabels: [],
        yAxisTicks: [],
        peakPoint: null
      }
    }
    const maxValue = Math.max(...points.map((item) => Number(item.online_users || 0)), 1)
    const { axisMax, ticks } = buildYAxisTicks(maxValue)
    const chartWidth = CURVE_CHART.width - CURVE_CHART.paddingLeft - CURVE_CHART.paddingRight
    const chartHeight = CURVE_CHART.height - CURVE_CHART.paddingTop - CURVE_CHART.paddingBottom
    const peakIndex = points.findIndex((item) => Number(item.online_users || 0) === maxValue)
    const peakPoint = peakIndex >= 0 ? {
      value: maxValue,
      x: CURVE_CHART.paddingLeft + getCurveX(peakIndex, points.length, chartWidth),
      y: CURVE_CHART.paddingTop + getCurveY(maxValue, chartHeight, axisMax)
    } : null

    return {
      path: buildCurvePath(points, chartWidth, chartHeight, axisMax),
      xAxisLabels: buildXAxisLabels(points, chartWidth, formatTimeText),
      yAxisTicks: ticks,
      maxValue,
      peakPoint
    }
  }, [todayOnlineCurve])

  const openOnlinePeakHistory = async () => {
    setDetailLoading(true)
    setDetailView('peak-history')
    try {
      const response = await api.get('/admin/registration-usage-summary/online-peak-history')
      if (response.data.success) {
        const data = response.data.data || response.data || {}
        setOnlinePeakHistory(Array.isArray(data.rows) ? data.rows : [])
      }
    } catch (error) {
      console.error('获取每日峰值历史失败:', error)
      setOnlinePeakHistory([])
    } finally {
      setDetailLoading(false)
    }
  }

  const sortOnlineUserList = (list) => {
    return [...list].sort((a, b) => {
      const aIsTencent = String(a.download_source_label || '').includes('腾讯') ? 0 : 1
      const bIsTencent = String(b.download_source_label || '').includes('腾讯') ? 0 : 1
      if (aIsTencent !== bIsTencent) return aIsTencent - bIsTencent
      return Number(b.total_recharge_amount || 0) - Number(a.total_recharge_amount || 0)
    })
  }

  const openTodayOnlineCurve = async (silent = false) => {
    if (!silent) setDetailLoading(true)
    if (!silent) setDetailView('today-online-curve')
    try {
      const response = await api.get('/admin/registration-usage-summary/today-online-curve')
      if (response.data.success) {
        const data = response.data.data || response.data || {}
        setTodayOnlineCurve({
          current_online_users: Number(data.current_online_users || 0),
          today_5min_online_peak: Number(data.today_5min_online_peak || 0),
          today_5min_online_peak_time: data.today_5min_online_peak_time || null,
          points: Array.isArray(data.points) ? data.points : [],
          online_user_list: sortOnlineUserList(Array.isArray(data.online_user_list) ? data.online_user_list : [])
        })
      }
    } catch (error) {
      console.error('获取今日在线曲线失败:', error)
      setTodayOnlineCurve({
        current_online_users: 0,
        today_5min_online_peak: 0,
        today_5min_online_peak_time: null,
        points: [],
        online_user_list: []
      })
    } finally {
      setDetailLoading(false)
    }
  }

  const renderOnlinePeakHistoryView = () => (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-xl font-bold text-gray-900">每日5分钟在线峰值</h3>
          <p className="mt-1 text-sm text-gray-500">展示数据库已记录的每天峰值时间点和峰值数值</p>
        </div>
        <button
          type="button"
          onClick={() => setDetailView('summary')}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
        >
          <ArrowLeft className="h-4 w-4" />
          返回概览
        </button>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">日期</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">峰值时间</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">峰值人数</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {detailLoading ? (
                <tr>
                  <td colSpan={3} className="px-6 py-10 text-center text-sm text-gray-500">
                    <Loader2 className="mx-auto h-6 w-6 animate-spin text-blue-600" />
                  </td>
                </tr>
              ) : onlinePeakHistory.length > 0 ? (
                onlinePeakHistory.map((item) => (
                  <tr key={item.usage_day} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">{item.usage_day}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{formatDateTimeText(item.peak_snapshot_minute)}</td>
                    <td className="px-6 py-4 text-sm font-semibold text-fuchsia-600">{item.peak_online_users || 0}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="px-6 py-10 text-center text-sm text-gray-500">
                    暂无峰值历史数据
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )

  const renderTodayOnlineCurveView = () => (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-xl font-bold text-gray-900">今日5分钟在线数曲线</h3>
          <p className="mt-1 text-sm text-gray-500">基于 `model_usage_online_snapshots` 的分钟快照绘制今日曲线</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => openTodayOnlineCurve()}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4" />
            刷新曲线
          </button>
          <button
            type="button"
            onClick={() => setDetailView('summary')}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <ArrowLeft className="h-4 w-4" />
            返回概览
          </button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">当前近5分钟在线数</div>
          <div className="mt-2 text-2xl font-bold text-purple-600">{todayOnlineCurve.current_online_users || 0}</div>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">今日峰值</div>
          <div className="mt-2 text-2xl font-bold text-fuchsia-600">{todayOnlineCurve.today_5min_online_peak || 0}</div>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">峰值时间</div>
          <div className="mt-2 text-2xl font-bold text-slate-700">{formatTimeText(todayOnlineCurve.today_5min_online_peak_time)}</div>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        {detailLoading ? (
          <div className="py-16 text-center text-sm text-gray-500">
            <Loader2 className="mx-auto h-6 w-6 animate-spin text-blue-600" />
          </div>
        ) : todayOnlineCurve.points.length > 0 ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between text-sm text-gray-500">
              <span>今日分钟级快照</span>
              <span>最大值 {curveChartData.maxValue}</span>
            </div>
            <div className="overflow-x-auto">
              <div className="min-w-[820px]">
                <svg viewBox={`0 0 ${CURVE_CHART.width} ${CURVE_CHART.height}`} className="h-72 w-full">
                  {curveChartData.yAxisTicks.map((tick) => {
                    const chartHeight = CURVE_CHART.height - CURVE_CHART.paddingTop - CURVE_CHART.paddingBottom
                    const y = CURVE_CHART.paddingTop + getCurveY(tick.value, chartHeight, curveChartData.yAxisTicks[curveChartData.yAxisTicks.length - 1]?.value || 1)
                    return (
                      <g key={`y-tick-${tick.value}`}>
                        <line
                          x1={CURVE_CHART.paddingLeft}
                          y1={y}
                          x2={CURVE_CHART.width - CURVE_CHART.paddingRight}
                          y2={y}
                          stroke="#e2e8f0"
                          strokeWidth="1"
                          strokeDasharray={tick.value === 0 ? undefined : '4 4'}
                        />
                        <text
                          x={CURVE_CHART.paddingLeft - 10}
                          y={y + 4}
                          textAnchor="end"
                          fontSize="12"
                          fill="#64748b"
                        >
                          {tick.value}
                        </text>
                      </g>
                    )
                  })}
                  <line
                    x1={CURVE_CHART.paddingLeft}
                    y1={CURVE_CHART.paddingTop}
                    x2={CURVE_CHART.paddingLeft}
                    y2={CURVE_CHART.height - CURVE_CHART.paddingBottom}
                    stroke="#cbd5e1"
                    strokeWidth="1"
                  />
                  <line
                    x1={CURVE_CHART.paddingLeft}
                    y1={CURVE_CHART.height - CURVE_CHART.paddingBottom}
                    x2={CURVE_CHART.width - CURVE_CHART.paddingRight}
                    y2={CURVE_CHART.height - CURVE_CHART.paddingBottom}
                    stroke="#cbd5e1"
                    strokeWidth="1"
                  />
                  <g transform={`translate(${CURVE_CHART.paddingLeft} ${CURVE_CHART.paddingTop})`}>
                    <path d={curveChartData.path} fill="none" stroke="#7c3aed" strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" />
                  </g>
                  {curveChartData.peakPoint ? (
                    <g>
                      <circle
                        cx={curveChartData.peakPoint.x}
                        cy={curveChartData.peakPoint.y}
                        r="4.5"
                        fill="#7c3aed"
                        stroke="#ffffff"
                        strokeWidth="2"
                      />
                      <text
                        x={Math.min(curveChartData.peakPoint.x + 8, CURVE_CHART.width - CURVE_CHART.paddingRight - 6)}
                        y={Math.max(curveChartData.peakPoint.y - 10, CURVE_CHART.paddingTop + 12)}
                        fontSize="12"
                        fontWeight="600"
                        fill="#7c3aed"
                      >
                        {curveChartData.peakPoint.value}
                      </text>
                    </g>
                  ) : null}
                  {curveChartData.xAxisLabels.map((item) => (
                    <g key={item.key}>
                      <line
                        x1={CURVE_CHART.paddingLeft + item.x}
                        y1={CURVE_CHART.height - CURVE_CHART.paddingBottom}
                        x2={CURVE_CHART.paddingLeft + item.x}
                        y2={CURVE_CHART.height - CURVE_CHART.paddingBottom + 5}
                        stroke="#cbd5e1"
                        strokeWidth="1"
                      />
                      <text
                        x={CURVE_CHART.paddingLeft + item.x}
                        y={CURVE_CHART.height - 8}
                        textAnchor={item.anchor}
                        fontSize="12"
                        fill="#64748b"
                      >
                        {item.text}
                      </text>
                    </g>
                  ))}
                </svg>
              </div>
            </div>
          </div>
        ) : (
          <div className="py-16 text-center text-sm text-gray-500">
            今日暂无在线快照数据
          </div>
        )}
      </div>

      {/* 当前在线用户列表 */}
      {!detailLoading && todayOnlineCurve.online_user_list.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <h4 className="text-sm font-semibold text-gray-900">当前在线用户（{todayOnlineCurve.online_user_list.length}）</h4>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">手机号/UID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">渠道来源</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">注册时间</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">已充值金额</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">用户标签</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {todayOnlineCurve.online_user_list.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">
                      <div>{item.phone || '-'}</div>
                      <div className="text-xs text-gray-500">UID: {item.id}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">{item.download_source_label || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{item.created_at || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{item.total_recharge_amount}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {Array.isArray(item.user_labels) && item.user_labels.length > 0
                        ? item.user_labels.join('、')
                        : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )

  // 卡片同刻对比数据（近5分钟在线数同刻值 + 截至当前时刻的峰值）
  const cmpYesterday = todayCompare?.yesterday || null
  const cmpLastWeek = todayCompare?.last_week_same_day || null
  const lastWeekSameMomentTitle = `上周${getWeekdayText(addDays(todayText, -7))}同刻数据`
  const yesterdayPeakTitle = cmpYesterday?.online_peak_time
    ? `昨日同刻峰值(${formatTimeText(cmpYesterday.online_peak_time)})`
    : '昨日同刻峰值'
  const lastWeekPeakTitle = cmpLastWeek?.online_peak_time
    ? `上周${getWeekdayText(addDays(todayText, -7))}同刻峰值(${formatTimeText(cmpLastWeek.online_peak_time)})`
    : `上周${getWeekdayText(addDays(todayText, -7))}同刻峰值`

  if (detailView === 'peak-history') {
    return renderOnlinePeakHistoryView()
  }

  if (detailView === 'today-online-curve') {
    return renderTodayOnlineCurveView()
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">注册与使用概览</h2>
          <p className="mt-1 text-sm text-gray-500">按起止日期查看注册、充值和使用情况</p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
            <CalendarDays className="h-4 w-4 text-gray-400" />
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="border-0 bg-transparent text-sm text-gray-700 outline-none"
            />
          </div>

          <span className="text-sm text-gray-400">～</span>

          <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
            <CalendarDays className="h-4 w-4 text-gray-400" />
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="border-0 bg-transparent text-sm text-gray-700 outline-none"
            />
          </div>

          <button type="submit" className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700">
            查询
          </button>
          <button
            type="button"
            onClick={() => handleQuickRange(7)}
            className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            最近7天
          </button>
          <button
            type="button"
            onClick={() => handleQuickRange(30)}
            className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            最近30天
          </button>
          <button
            type="button"
            onClick={handleReset}
            className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            重置
          </button>
          <button
            type="button"
            onClick={() => fetchData(filters)}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4" />
            刷新
          </button>
          <button
            type="button"
            onClick={handleExport}
            disabled={loading || displayDaily.length === 0}
            className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-700 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400"
          >
            <Download className="h-4 w-4" />
            导出 Excel
          </button>
        </form>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white px-5 py-4 text-sm text-gray-600 shadow-sm">
        当前区间：{rangeText}
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-7">
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">注册总数</div>
          <div className="mt-2 flex items-center gap-2 text-2xl font-bold text-blue-600">
            <Users className="h-5 w-5" />
            {loading ? <Loader2 className="h-6 w-6 animate-spin text-gray-400" /> : summary.total_registered || 0}
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">充值用户总数</div>
          <div className="mt-2 flex items-center gap-2 text-2xl font-bold text-orange-600">
            <Wallet className="h-5 w-5" />
            {loading ? <Loader2 className="h-6 w-6 animate-spin text-gray-400" /> : summary.total_recharge_users || 0}
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">(官网/360/360(top))支付额</div>
          <div className="mt-2 flex flex-col gap-1">
            {loading ? (
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            ) : (
              <>
                <div className="text-2xl font-bold text-emerald-600">{formatAmount(summary.total_official_recharge_amount)}</div>
                <div className="text-2xl font-bold text-blue-600">{formatAmount(summary.total_ly360_recharge_amount)}</div>
                <div className="text-2xl font-bold text-sky-600">{formatAmount(summary.total_ly360top_recharge_amount)}</div>
              </>
            )}
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">(官网/360/360(top))新用户支付额</div>
          <div className="mt-2 flex flex-col gap-1">
            {loading ? (
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            ) : (
              <>
                <div className="text-2xl font-bold text-amber-600">{formatAmount(summary.total_same_day_official_amount)}</div>
                <div className="text-2xl font-bold text-blue-600">{formatAmount(summary.total_same_day_ly360_amount)}</div>
                <div className="text-2xl font-bold text-sky-600">{formatAmount(summary.total_same_day_ly360top_amount)}</div>
              </>
            )}
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">近5分钟在线数</div>
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={() => openTodayOnlineCurve()}
              className="inline-flex items-center gap-2 text-left text-2xl font-bold text-purple-600 hover:opacity-80"
            >
              <BarChart3 className="h-5 w-5" />
              {loading ? <Loader2 className="h-6 w-6 animate-spin text-gray-400" /> : summary.current_online_users || 0}
            </button>
            {!loading && (cmpYesterday?.current_online_users != null || cmpLastWeek?.current_online_users != null) && (
              <span className="text-base font-semibold">
                （
                <span className="cursor-help text-blue-500" title="昨日同刻数据">{cmpYesterday?.current_online_users ?? '-'}</span>
                <span className="text-gray-400">/</span>
                <span className="cursor-help text-orange-500" title={lastWeekSameMomentTitle}>{cmpLastWeek?.current_online_users ?? '-'}</span>
                ）
              </span>
            )}
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">第二天使用人数</div>
          <div className="mt-2 text-2xl font-bold text-sky-600">
            {loading ? <Loader2 className="h-6 w-6 animate-spin text-gray-400" /> : summary.total_second_day_usage_users || 0}
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">今日5分钟在线峰值</div>
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={openOnlinePeakHistory}
              className="inline-flex items-center gap-2 text-left text-2xl font-bold text-fuchsia-600 hover:opacity-80"
            >
              <BarChart3 className="h-5 w-5" />
              {loading ? <Loader2 className="h-6 w-6 animate-spin text-gray-400" /> : summary.today_5min_online_peak || 0}
            </button>
            {!loading && (cmpYesterday?.online_peak != null || cmpLastWeek?.online_peak != null) && (
              <span className="text-base font-semibold">
                （
                <span className="cursor-help text-blue-500" title={yesterdayPeakTitle}>{cmpYesterday?.online_peak ?? '-'}</span>
                <span className="text-gray-400">/</span>
                <span className="cursor-help text-orange-500" title={lastWeekPeakTitle}>{cmpLastWeek?.online_peak ?? '-'}</span>
                ）
              </span>
            )}
          </div>
          <div className="mt-2 text-sm text-gray-500">
            时间：{loading ? '--' : formatDateTimeText(summary.today_5min_online_peak_time)}
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">日期</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">注册人数</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">充值用户数</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">(首充/多充)用户数</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">(首充/多充)新用户支付数</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">(今日/非今日)首充</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">(官网/360/360(top))支付额</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">(官网/360/360(top))新用户支付额</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">使用人数(含老用户）</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">第二天使用人数</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">老用户使用人数</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">(已充值/未充值)老用户</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">重度用户使用人数</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {loading ? (
                <tr>
                  <td colSpan={12} className="px-6 py-10 text-center text-sm text-gray-500">
                    <Loader2 className="mx-auto h-6 w-6 animate-spin text-blue-600" />
                  </td>
                </tr>
              ) : displayDaily.length > 0 ? (
                displayDaily.map((item) => {
                  const isTodayRow = item.date === todayText
                  const cmpYesterday = isTodayRow && todayCompare ? todayCompare.yesterday : null
                  const cmpLastWeek = isTodayRow && todayCompare ? todayCompare.last_week_same_day : null
                  return (
                    <tr key={item.date} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">{item.date}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <div>{item.registered_count || 0}</div>
                        {cmpYesterday && <CompareLine yesterdayValue={cmpYesterday.registered_count || 0} lastWeekValue={cmpLastWeek.registered_count || 0} lastWeekTitle={lastWeekTitle} />}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <div>{item.recharge_user_count || 0}</div>
                        {cmpYesterday && <CompareLine yesterdayValue={cmpYesterday.recharge_user_count || 0} lastWeekValue={cmpLastWeek.recharge_user_count || 0} lastWeekTitle={lastWeekTitle} />}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {(item.first_recharge_user_count || 0)}/{(item.repeat_recharge_user_count || 0)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {(item.new_user_first_only_count || 0)}/{(item.new_user_repeat_count || 0)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {(item.first_recharge_same_day_count || 0)}/{(item.first_recharge_diff_day_count || 0)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <div>{formatAmount(item.official_recharge_amount)}/{formatAmount(item.ly360_recharge_amount)}/{formatAmount(item.ly360top_recharge_amount)}</div>
                        {cmpYesterday && <CompareLine yesterdayValue={formatAmountOne(cmpYesterday.official_recharge_amount)} lastWeekValue={formatAmountOne(cmpLastWeek.official_recharge_amount)} lastWeekTitle={lastWeekTitle} />}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <div>{formatAmount(item.same_day_official_amount)}/{formatAmount(item.same_day_ly360_amount)}/{formatAmount(item.same_day_ly360top_amount)}</div>
                        {cmpYesterday && <CompareLine yesterdayValue={formatAmountOne(cmpYesterday.same_day_official_amount)} lastWeekValue={formatAmountOne(cmpLastWeek.same_day_official_amount)} lastWeekTitle={lastWeekTitle} />}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <div>{item.usage_user_count || 0}</div>
                        {cmpYesterday && <CompareLine yesterdayValue={cmpYesterday.usage_user_count || 0} lastWeekValue={cmpLastWeek.usage_user_count || 0} lastWeekTitle={lastWeekTitle} />}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <div>{item.second_day_usage_user_count || 0}</div>
                        {cmpYesterday && <CompareLine yesterdayValue={cmpYesterday.second_day_usage_user_count || 0} lastWeekValue={cmpLastWeek.second_day_usage_user_count || 0} lastWeekTitle={lastWeekTitle} />}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <div>{item.old_usage_user_count || 0}</div>
                        {cmpYesterday && <CompareLine yesterdayValue={cmpYesterday.old_usage_user_count || 0} lastWeekValue={cmpLastWeek.old_usage_user_count || 0} lastWeekTitle={lastWeekTitle} />}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {(item.paid_old_usage_user_count || 0)}/{(item.unpaid_old_usage_user_count || 0)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <div>{item.heavy_usage_user_count || 0}</div>
                        {cmpYesterday && <CompareLine yesterdayValue={cmpYesterday.heavy_usage_user_count || 0} lastWeekValue={cmpLastWeek.heavy_usage_user_count || 0} lastWeekTitle={lastWeekTitle} />}
                      </td>
                    </tr>
                  )
                })
              ) : (
                <tr>
                  <td colSpan={12} className="px-6 py-10 text-center text-sm text-gray-500">
                    当前区间暂无统计数据
                  </td>
                </tr>
              )}
            </tbody>
            {!loading && displayDaily.length > 0 ? (
              <tfoot className="bg-gray-50">
                <tr>
                  <td className="px-6 py-4 text-sm font-semibold text-gray-900">合计</td>
                  <td className="px-6 py-4 text-sm font-semibold text-gray-900">{summary.total_registered || 0}</td>
                  <td className="px-6 py-4 text-sm font-semibold text-gray-900">{summary.total_recharge_users || 0}</td>
                  <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                    {(summary.total_first_recharge_users || 0)}/{(summary.total_repeat_recharge_users || 0)}
                  </td>
                  <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                    {(summary.total_new_user_first_only || 0)}/{(summary.total_new_user_repeat || 0)}
                  </td>
                  <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                    {(summary.total_first_recharge_same_day || 0)}/{(summary.total_first_recharge_diff_day || 0)}
                  </td>
                  <td className="px-6 py-4 text-sm font-semibold text-gray-900">{formatAmount(summary.total_official_recharge_amount)}/{formatAmount(summary.total_ly360_recharge_amount)}/{formatAmount(summary.total_ly360top_recharge_amount)}</td>
                  <td className="px-6 py-4 text-sm font-semibold text-gray-900">{formatAmount(summary.total_same_day_official_amount)}/{formatAmount(summary.total_same_day_ly360_amount)}/{formatAmount(summary.total_same_day_ly360top_amount)}</td>
                  <td className="px-6 py-4 text-sm font-semibold text-gray-900">{summary.total_usage_users || 0}</td>
                  <td className="px-6 py-4 text-sm font-semibold text-gray-900">{summary.total_second_day_usage_users || 0}</td>
                  <td className="px-6 py-4 text-sm font-semibold text-gray-900">{summary.total_old_usage_users || 0}</td>
                  <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                    {(summary.total_paid_old_usage_users || 0)}/{(summary.total_unpaid_old_usage_users || 0)}
                  </td>
                  <td className="px-6 py-4 text-sm font-semibold text-gray-900">{summary.total_heavy_usage_users || 0}</td>
                </tr>
              </tfoot>
            ) : null}
          </table>
        </div>
      </div>
    </div>
  )
}
